/**
 * WorkflowVersioning - Git-like version control UI
 * ZAPHUB 2.0 - Version history, diff viewer, rollback
 */

import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  GitBranch,
  GitCommit,
  Tag,
  RotateCcw,
  Download,
  Upload,
  Clock,
  User,
  CheckCircle,
  Circle,
  ArrowRight,
  Plus,
  Minus,
  Edit,
} from 'lucide-react';
import type { WorkflowVersion, WorkflowDiff, Workflow } from '@shared/types';

interface WorkflowVersioningProps {
  workflow: Workflow;
  onRestore?: (versionId: string) => void;
}

export const WorkflowVersioning: React.FC<WorkflowVersioningProps> = ({ workflow, onRestore }) => {
  const [versions, setVersions] = useState<WorkflowVersion[]>([]);
  const [selectedVersionA, setSelectedVersionA] = useState<number | null>(null);
  const [selectedVersionB, setSelectedVersionB] = useState<number | null>(null);
  const [diff, setDiff] = useState<WorkflowDiff | null>(null);
  const [showCommitDialog, setShowCommitDialog] = useState(false);
  const [showTagDialog, setShowTagDialog] = useState(false);
  const [commitMessage, setCommitMessage] = useState('');
  const [tagName, setTagName] = useState('');
  const [selectedVersion, setSelectedVersion] = useState<WorkflowVersion | null>(null);

  useEffect(() => {
    fetchVersionHistory();
  }, [workflow.id]);

  const fetchVersionHistory = async () => {
    // Mock data - in production, call API
    const mockVersions: WorkflowVersion[] = [
      {
        id: 'v5',
        workflowId: workflow.id,
        version: 5,
        name: workflow.name,
        description: workflow.description,
        nodes: workflow.nodes,
        createdBy: 'john.doe',
        createdAt: new Date(Date.now() - 1 * 60 * 60 * 1000), // 1 hour ago
        tag: 'production',
        commitMessage: 'Deploy to production - stable release',
        isActive: true,
      },
      {
        id: 'v4',
        workflowId: workflow.id,
        version: 4,
        name: workflow.name,
        description: workflow.description,
        nodes: workflow.nodes,
        createdBy: 'john.doe',
        createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000), // 3 hours ago
        tag: 'v2.1.0',
        commitMessage: 'Add error handling to email node',
        parentVersionId: 'v3',
        isActive: false,
      },
      {
        id: 'v3',
        workflowId: workflow.id,
        version: 3,
        name: workflow.name,
        description: workflow.description,
        nodes: workflow.nodes,
        createdBy: 'jane.smith',
        createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
        commitMessage: 'Optimize database queries',
        parentVersionId: 'v2',
        isActive: false,
      },
      {
        id: 'v2',
        workflowId: workflow.id,
        version: 2,
        name: workflow.name,
        description: workflow.description,
        nodes: workflow.nodes,
        createdBy: 'john.doe',
        createdAt: new Date(Date.now() - 48 * 60 * 60 * 1000), // 2 days ago
        tag: 'v2.0.0',
        commitMessage: 'Major refactor - add parallel execution',
        parentVersionId: 'v1',
        isActive: false,
      },
      {
        id: 'v1',
        workflowId: workflow.id,
        version: 1,
        name: workflow.name,
        description: workflow.description,
        nodes: [],
        createdBy: 'john.doe',
        createdAt: new Date(Date.now() - 168 * 60 * 60 * 1000), // 1 week ago
        tag: 'v1.0.0',
        commitMessage: 'Initial commit',
        isActive: false,
      },
    ];

    setVersions(mockVersions);
  };

  const handleCreateVersion = async () => {
    if (!commitMessage.trim()) {
      alert('Please enter a commit message');
      return;
    }

    // Mock - in production, call API
    console.log('Creating new version:', commitMessage);
    setShowCommitDialog(false);
    setCommitMessage('');
    fetchVersionHistory();
  };

  const handleTagVersion = async () => {
    if (!tagName.trim() || !selectedVersion) {
      alert('Please enter a tag name');
      return;
    }

    // Mock - in production, call API
    console.log('Tagging version', selectedVersion.version, 'as', tagName);
    setShowTagDialog(false);
    setTagName('');
    fetchVersionHistory();
  };

  const handleRollback = async (versionId: string) => {
    if (!confirm('Are you sure you want to rollback to this version?')) {
      return;
    }

    if (onRestore) {
      onRestore(versionId);
    }
  };

  const handleCompare = () => {
    if (selectedVersionA === null || selectedVersionB === null) {
      alert('Please select two versions to compare');
      return;
    }

    // Mock diff - in production, call API
    const mockDiff: WorkflowDiff = {
      versionA: selectedVersionA,
      versionB: selectedVersionB,
      changes: {
        nodesAdded: [
          {
            id: 'node-new-1',
            type: 'EMAIL' as any,
            name: 'Send Confirmation Email',
            config: {},
            position: { x: 100, y: 100 },
            connections: [],
          },
        ],
        nodesRemoved: [
          {
            id: 'node-old-1',
            type: 'NOTIFICATION' as any,
            name: 'Old Notification',
            config: {},
            position: { x: 50, y: 50 },
            connections: [],
          },
        ],
        nodesModified: [
          {
            nodeId: 'node-mod-1',
            before: {
              id: 'node-mod-1',
              type: 'AI_AGENT' as any,
              name: 'AI Agent - Old Config',
              config: { temperature: 0.7 },
              position: { x: 200, y: 200 },
              connections: [],
            },
            after: {
              id: 'node-mod-1',
              type: 'AI_AGENT' as any,
              name: 'AI Agent - New Config',
              config: { temperature: 0.9 },
              position: { x: 200, y: 200 },
              connections: [],
            },
          },
        ],
      },
    };

    setDiff(mockDiff);
  };

  const handleExport = async () => {
    // Export version history as JSON
    const data = JSON.stringify(versions, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${workflow.name}-version-history.json`;
    a.click();
  };

  const formatDate = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl mb-1">Version Control</h2>
          <p className="text-gray-600">Git-like versioning for {workflow.name}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export History
          </Button>
          <Button onClick={() => setShowCommitDialog(true)}>
            <GitCommit className="h-4 w-4 mr-2" />
            Create Version
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
              <GitBranch className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <div className="text-2xl">{versions.length}</div>
              <div className="text-sm text-gray-600">Total Versions</div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <div className="text-2xl">v{versions[0]?.version || 1}</div>
              <div className="text-sm text-gray-600">Current Version</div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Tag className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl">{versions.filter((v) => v.tag).length}</div>
              <div className="text-sm text-gray-600">Tagged Versions</div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
              <User className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <div className="text-2xl">
                {new Set(versions.map((v) => v.createdBy)).size}
              </div>
              <div className="text-sm text-gray-600">Contributors</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="history" className="w-full">
        <TabsList>
          <TabsTrigger value="history">History</TabsTrigger>
          <TabsTrigger value="compare">Compare</TabsTrigger>
          <TabsTrigger value="changelog">Changelog</TabsTrigger>
        </TabsList>

        <TabsContent value="history" className="mt-6">
          <Card className="p-6">
            <div className="space-y-4">
              {versions.map((version, index) => (
                <div
                  key={version.id}
                  className="flex items-start gap-4 pb-4 border-b last:border-b-0"
                >
                  {/* Timeline */}
                  <div className="flex flex-col items-center">
                    <div
                      className={`h-10 w-10 rounded-full flex items-center justify-center ${
                        version.isActive
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {version.isActive ? (
                        <CheckCircle className="h-5 w-5" />
                      ) : (
                        <Circle className="h-5 w-5" />
                      )}
                    </div>
                    {index < versions.length - 1 && (
                      <div className="w-0.5 h-full bg-gray-200 mt-2" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg">Version {version.version}</span>
                          {version.tag && (
                            <Badge variant="secondary">
                              <Tag className="h-3 w-3 mr-1" />
                              {version.tag}
                            </Badge>
                          )}
                          {version.isActive && (
                            <Badge variant="default" className="bg-green-500">
                              Active
                            </Badge>
                          )}
                        </div>
                        <p className="text-gray-700">{version.commitMessage}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                      <div className="flex items-center gap-1">
                        <User className="h-4 w-4" />
                        <span>{version.createdBy}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>{formatDate(version.createdAt)}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {!version.isActive && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRollback(version.id)}
                          >
                            <RotateCcw className="h-4 w-4 mr-2" />
                            Rollback
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedVersion(version);
                              setShowTagDialog(true);
                            }}
                          >
                            <Tag className="h-4 w-4 mr-2" />
                            Add Tag
                          </Button>
                        </>
                      )}
                      <Button variant="ghost" size="sm">
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="compare" className="mt-6">
          <div className="space-y-6">
            {/* Version Selectors */}
            <Card className="p-6">
              <h3 className="text-lg mb-4">Select Versions to Compare</h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <Label>Version A (Older)</Label>
                  <select
                    className="w-full mt-1 p-2 border rounded-lg"
                    value={selectedVersionA || ''}
                    onChange={(e) => setSelectedVersionA(Number(e.target.value))}
                  >
                    <option value="">Select version...</option>
                    {versions.map((v) => (
                      <option key={v.id} value={v.version}>
                        v{v.version} - {v.tag || 'No tag'}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Version B (Newer)</Label>
                  <select
                    className="w-full mt-1 p-2 border rounded-lg"
                    value={selectedVersionB || ''}
                    onChange={(e) => setSelectedVersionB(Number(e.target.value))}
                  >
                    <option value="">Select version...</option>
                    {versions.map((v) => (
                      <option key={v.id} value={v.version}>
                        v{v.version} - {v.tag || 'No tag'}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <Button onClick={handleCompare} className="w-full">
                <ArrowRight className="h-4 w-4 mr-2" />
                Compare Versions
              </Button>
            </Card>

            {/* Diff Display */}
            {diff && (
              <Card className="p-6">
                <h3 className="text-lg mb-4">
                  Changes from v{diff.versionA} to v{diff.versionB}
                </h3>

                {/* Added Nodes */}
                {diff.changes.nodesAdded.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Plus className="h-5 w-5 text-green-600" />
                      <h4 className="text-green-600">
                        Added Nodes ({diff.changes.nodesAdded.length})
                      </h4>
                    </div>
                    {diff.changes.nodesAdded.map((node) => (
                      <div key={node.id} className="bg-green-50 border-l-4 border-green-500 p-4 mb-2">
                        <div className="flex items-center gap-2">
                          <Plus className="h-4 w-4 text-green-600" />
                          <span className="text-green-900">{node.name}</span>
                          <Badge variant="secondary">{node.type}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Removed Nodes */}
                {diff.changes.nodesRemoved.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Minus className="h-5 w-5 text-red-600" />
                      <h4 className="text-red-600">
                        Removed Nodes ({diff.changes.nodesRemoved.length})
                      </h4>
                    </div>
                    {diff.changes.nodesRemoved.map((node) => (
                      <div key={node.id} className="bg-red-50 border-l-4 border-red-500 p-4 mb-2">
                        <div className="flex items-center gap-2">
                          <Minus className="h-4 w-4 text-red-600" />
                          <span className="text-red-900 line-through">{node.name}</span>
                          <Badge variant="secondary">{node.type}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Modified Nodes */}
                {diff.changes.nodesModified.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Edit className="h-5 w-5 text-blue-600" />
                      <h4 className="text-blue-600">
                        Modified Nodes ({diff.changes.nodesModified.length})
                      </h4>
                    </div>
                    {diff.changes.nodesModified.map((change) => (
                      <div key={change.nodeId} className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-2">
                        <div className="mb-2">
                          <Badge variant="secondary">{change.before.type}</Badge>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Minus className="h-4 w-4 text-red-600" />
                            <span className="text-red-900 line-through">
                              {change.before.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Plus className="h-4 w-4 text-green-600" />
                            <span className="text-green-900">{change.after.name}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {diff.changes.nodesAdded.length === 0 &&
                  diff.changes.nodesRemoved.length === 0 &&
                  diff.changes.nodesModified.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No changes between these versions
                    </div>
                  )}
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="changelog" className="mt-6">
          <Card className="p-6">
            <h3 className="text-lg mb-4">Changelog</h3>
            <div className="space-y-4">
              {versions.map((version) => (
                <div key={version.id} className="border-l-4 border-purple-500 pl-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">v{version.version}</span>
                    {version.tag && (
                      <Badge variant="secondary">
                        <Tag className="h-3 w-3 mr-1" />
                        {version.tag}
                      </Badge>
                    )}
                    <span className="text-sm text-gray-500">
                      - {formatDate(version.createdAt)}
                    </span>
                  </div>
                  <p className="text-gray-700">{version.commitMessage}</p>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Version Dialog */}
      <Dialog open={showCommitDialog} onOpenChange={setShowCommitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Version</DialogTitle>
            <DialogDescription>
              Create a snapshot of the current workflow state
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Commit Message</Label>
              <Textarea
                placeholder="Describe what changed in this version..."
                value={commitMessage}
                onChange={(e) => setCommitMessage(e.target.value)}
                rows={3}
              />
            </div>
            <div>
              <Label>Tag (Optional)</Label>
              <Input
                placeholder="e.g., v2.0.0, production, staging"
                value={tagName}
                onChange={(e) => setTagName(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCommitDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateVersion}>
              <GitCommit className="h-4 w-4 mr-2" />
              Create Version
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tag Version Dialog */}
      <Dialog open={showTagDialog} onOpenChange={setShowTagDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Tag to Version</DialogTitle>
            <DialogDescription>
              Tag version {selectedVersion?.version} for easy reference
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Label>Tag Name</Label>
            <Input
              placeholder="e.g., v2.0.0, production, stable"
              value={tagName}
              onChange={(e) => setTagName(e.target.value)}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTagDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleTagVersion}>
              <Tag className="h-4 w-4 mr-2" />
              Add Tag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
